import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Modal, ModalBody, ModalFooter} from "reactstrap";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {useTranslation} from "react-i18next";
import "./cancelPostponeModal.sass";
import ModalDetailHeader from "../../molecules/modalDetailHeader/modalDetailHeader";
import {useStores} from "../../../stores/useStores";
import parse from "html-react-parser";
import DateEdit from "../dateEdit/dateEdit";
import Flex from "../../atoms/common/flex";
import classNames from "classnames";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import {UpdateRecurringEventOption} from "../../../shared/enums/updateRecurringEventOption";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import TextEdit from "../textEdit/textEdit";
import {EventStatus} from "../../../shared/enums/eventStatus";
import {runInAction} from "mobx";

export interface ICancelPostponeModalProps {
  isOpen: boolean;
  eventStatus: EventStatus;
  toggle: () => void;
  onConfirmBtnClicked: (effectiveFromDate: Date) => void;
}

const CancelPostponeModal: React.FC<ICancelPostponeModalProps> = ({
  isOpen,
  eventStatus,
  toggle,
  onConfirmBtnClicked
}) => {
  const {t} = useTranslation(["translation", "event"]);
  const [effectiveFromDate] = useState(() => new DateObservable());

  const {
    uiStore: {modalStore, eventsUiStore},
    domainStore: {eventsDomainStore}
  } = useStores();

  const eventTitle = eventsDomainStore.editableEvent.title.text;
  const eventIsBookable =
    eventsDomainStore.editableEvent.bookingDetails.hasBookingDeadline;
  const eventIsPaid =
    eventsDomainStore.editableEvent.priceDetails.paidType.text ===
    EventPaidType.fixed;

  const localNow = eventsDomainStore.getCurrentTime();

  // Get the event's end datetime
  const eventEndTime = new Date(
    eventsDomainStore.editableEvent.eventDateDetails.endDate.initialValue
  );

  const selectableOccurrenceDates = eventsDomainStore.eventOccurrenceInfos
    .filter((d) => {
      // eventOccurrenceDates are start datetimes
      // Get occurrence's end datetime
      eventEndTime.setFullYear(
        d.occurrenceDate.getFullYear(),
        d.occurrenceDate.getMonth(),
        d.occurrenceDate.getDate()
      );
      // Filter events that haven't ended
      return localNow < eventEndTime;
    })
    .map((d) => d.occurrenceDate);

  if (selectableOccurrenceDates.length && !effectiveFromDate.date) {
    const firstOccurrenceDate = selectableOccurrenceDates[0];
    runInAction(() => {
      effectiveFromDate.date = firstOccurrenceDate;
    });
  }

  // all bookings (confirmed and waitlisted) are notified
  // refunds is number of paid bookings
  let totalNumberOfNotifications = 0;
  let totalNumberOfRefunds = 0;

  if (eventsUiStore.isEditingEventSeries) {
    const toBeCancelPostponedOccurrenceInfos =
      eventsDomainStore.eventOccurrenceInfos.filter(
        (d) => d.occurrenceDate >= effectiveFromDate.date!
      );

    toBeCancelPostponedOccurrenceInfos
      .filter(
        (value) =>
          value.occurrenceDate <
          eventsDomainStore.getNextReleasePeriodStartDate()!
      )
      .forEach((occurrenceInfo) => {
        totalNumberOfNotifications += occurrenceInfo.totalBookings ?? 0;
        totalNumberOfRefunds += occurrenceInfo.totalPaidBookings ?? 0;
      });
  } else {
    totalNumberOfNotifications =
      eventsUiStore.confirmedEventBookings?.length +
      eventsUiStore.waitlistEventBookings?.length;

    totalNumberOfRefunds = eventsUiStore.confirmedEventBookings.filter(
      (b) => b.isPaid
    ).length;
  }

  useEffect(() => {
    if (isOpen) {
      if (
        eventsUiStore.isEditingEventSeries &&
        eventsDomainStore.editableEvent.isReleased
      ) {
        eventsDomainStore.setEventUpdateOption(
          UpdateRecurringEventOption.FromOccurrenceDate
        );
      }
      if (eventStatus === EventStatus.Cancelled) {
        eventsDomainStore.editableEvent.reason.setText(
          eventIsPaid
            ? t("event:messages.cancellingEventTemplate", {
                fixedPriceTemplate: t(
                  "event:messages.fixedPriceEventCancellationTemplate"
                ),
                eventTitle: eventTitle
              })
            : t("event:messages.cancellingEventTemplate", {
                fixedPriceTemplate: "",
                eventTitle: eventTitle
              })
        );
      } else {
        eventsDomainStore.editableEvent.reason.setText(
          eventIsPaid
            ? t("event:messages.postponingEventTemplate", {
                fixedPriceTemplate: t(
                  "event:messages.fixedPriceEventPostponeTemplate"
                ),
                eventTitle: eventTitle
              })
            : t("event:messages.postponingEventTemplate", {
                fixedPriceTemplate: "",
                eventTitle: eventTitle
              })
        );
      }
    }
  }, [isOpen]);
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="event-detail-modal"
      centered
    >
      <ModalDetailHeader isLoading={false} toggle={toggle}>
        <div className="header-container">
          <div className="header-column column-dynamic">
            {EventDetailsHelper.getCancelPostponeModalTitle(
              t,
              eventStatus,
              eventsUiStore.isEditingEventSeries
            )}
          </div>
        </div>
      </ModalDetailHeader>
      <ModalBody>
        <>
          <div
            className="cancel-postpone-message"
            data-testid="cancelPostponeInfoMessage"
          >
            {parse(
              EventDetailsHelper.getCancelPostponeModalMessage(
                t,
                eventStatus,
                eventTitle,
                eventIsBookable,
                eventIsPaid,
                totalNumberOfNotifications,
                totalNumberOfRefunds,
                eventsUiStore.isEditingEventSeries
              )
            )}
          </div>
          <div className="date-picker-container">
            {eventsUiStore.isEditingEventSeries &&
              eventsDomainStore.editableEvent.isRecurringEvent && (
                <DateEdit
                  enabledDates={selectableOccurrenceDates}
                  value={effectiveFromDate}
                  label={t("event:labels.effectiveFrom")}
                  dateFormat={"dd/MM/yyyy"}
                  placeholder={t("event:placeholders.effectiveFromDate")}
                  overrideIsRequired={true}
                  showTimeSelect={false}
                  preventManualEntry={true}
                  timeZone={eventsDomainStore.getTimeZone()}
                />
              )}
          </div>
          <div className="cancel-postpone-textbox">
            <TextEdit
              type="textarea"
              value={eventsDomainStore.editableEvent.reason}
              validationMessage={t("event:errors.reason")}
              label={t("event:labels.reason")}
              placeholder={EventDetailsHelper.getCancelPostponeReasonPlaceholder(
                t,
                eventStatus,
                eventsUiStore.isEditingEventSeries
              )}
            />
          </div>
        </>
      </ModalBody>
      <ModalFooter
        tag={Flex}
        justify={modalStore.footerButtonAlignment}
        className={classNames("cancel-postpone-modal-footer")}
      >
        <ButtonIcon
          type={"primary-negative"}
          onClick={() => {
            onConfirmBtnClicked(effectiveFromDate.date!);
          }}
          isDisabled={
            !eventsDomainStore.editableEvent.reason.isValid ||
            (eventsUiStore.isEditingEventSeries && !effectiveFromDate.date)
          }
        >
          {EventDetailsHelper.getCancelPostponeModalPrimaryButtonText(
            t,
            eventStatus,
            eventsUiStore.isEditingEventSeries
          )}
        </ButtonIcon>
      </ModalFooter>
    </Modal>
  );
};

export default observer(CancelPostponeModal);
